import { useEffect, useState } from 'react';
import SummaryApi from '../common';
import Pagination from '../components/Pagination';
import { request } from '../helpers/request';

const AdminLogs = () => {
    const [logs, setLogs] = useState([]);
    const [loading, setLoading] = useState(true); 
    const [error, setError] = useState(null); 
    const [pagination, setPagination] = useState({
        total: 0,
        page: 1,
        pages: 1,
        limit: 50
    });

    const fetchLogs = async (page = 1) => {
        setLoading(true);
        try {
            const response = await request({
                ...SummaryApi.fetchAuditLogs,
                url: `${SummaryApi.fetchAuditLogs.url}?page=${page}&limit=50`
            });
            const resData = await response.json();
            if (resData.success) {
                setLogs(resData.logs);
                setPagination(resData.pagination);
            } else {
                setError(resData.message || 'Failed to load logs.');
            }
        } catch (err) {
            console.error('Failed to fetch logs:', err);
            setError('Failed to load logs.'); 
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchLogs();
    }, []);

    const handlePageChange = (newPage) => {
        fetchLogs(newPage);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    if (loading) {
        return <div className='p-8'><p className='animate-pulse text-gray-500 font-bold'>Loading audit logs...</p></div>; 
    }

    if (error) {
        return <div className='p-8'><p className='text-red-500 font-bold'>{error}</p></div>; 
    }

    return (
        <div className="p-4 md:p-8 space-y-6 md:space-y-8">
            <div className='space-y-1.5 md:space-y-2'>
                <h2 className='text-3xl md:text-4xl font-black text-primary tracking-tight'>Audit Log</h2>
                <p className='text-[11px] md:text-sm text-gray-400 font-bold uppercase tracking-widest'>Secure System Intelligence</p>
            </div>

            {/* Desktop Table View */}
            <div className='hidden lg:block bg-white rounded-[2.5rem] shadow-premium border border-surface-border overflow-hidden'>
                <div className='overflow-x-auto'>
                    {logs.length === 0 ? (
                        <div className='p-12 text-center text-gray-400 font-medium'>No logs available.</div> 
                    ) : (
                        <table className='w-full text-left'>
                            <thead>
                                <tr className='bg-surface-muted/50 border-b border-surface-border'>
                                    <th className='px-8 py-6 text-xs font-black uppercase tracking-widest text-gray-400'>Timestamp</th>
                                    <th className='px-8 py-6 text-xs font-black uppercase tracking-widest text-gray-400'>Action</th>
                                    <th className='px-8 py-6 text-xs font-black uppercase tracking-widest text-gray-400'>Details</th>
                                </tr>
                            </thead>
                            <tbody className='divide-y divide-surface-border text-sm'>
                                {logs.map((log) => (
                                    <tr key={log._id} className='hover:bg-surface-muted/30 transition-colors'>
                                        <td className='px-8 py-6 font-medium text-gray-600 whitespace-nowrap'>
                                            {new Date(log.timestamp || log.createdAt).toLocaleString()}
                                        </td>
                                        <td className='px-8 py-6'>
                                            <span className='px-3 py-1 bg-primary/5 text-primary text-[10px] font-black uppercase tracking-widest rounded-full border border-primary/10'>
                                                {log.action}
                                            </span>
                                        </td>
                                        <td className='px-8 py-6'>
                                            <div className='max-w-md truncate font-mono text-[11px] text-gray-500 bg-surface-muted/50 p-2 rounded-lg' title={JSON.stringify(log.details)}>
                                                {typeof log.details === 'object' ? JSON.stringify(log.details) : log.details}
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
            </div>

            {/* Mobile Card View */}
            <div className='lg:hidden space-y-4'>
                {logs.length === 0 ? (
                    <div className='bg-white rounded-3xl p-12 text-center text-gray-400 font-medium border border-surface-border'>No logs available.</div>
                ) : (
                    logs.map((log) => (
                        <div key={log._id} className='bg-white rounded-3xl p-6 border border-surface-border shadow-sm space-y-4'>
                            <div className='flex items-center justify-between'>
                                <span className='px-3 py-1 bg-primary/5 text-primary text-[10px] font-black uppercase tracking-widest rounded-full border border-primary/10'>
                                    {log.action}
                                </span>
                                <span className='text-[10px] font-bold text-gray-400'>
                                    {new Date(log.timestamp || log.createdAt).toLocaleDateString()}
                                </span>
                            </div>
                            
                            <div className='space-y-1'>
                                <p className='text-[10px] font-black uppercase tracking-widest text-gray-400'>Time</p>
                                <p className='text-xs font-bold text-gray-600'>
                                    {new Date(log.timestamp || log.createdAt).toLocaleTimeString()}
                                </p>
                            </div>

                            <div className='space-y-1'>
                                <p className='text-[10px] font-black uppercase tracking-widest text-gray-400'>Metadata</p>
                                <div className='font-mono text-[10px] text-gray-500 bg-surface-muted/50 p-3 rounded-xl overflow-x-auto'>
                                    {typeof log.details === 'object' ? JSON.stringify(log.details, null, 2) : log.details}
                                </div>
                            </div>
                        </div>
                    )
                ))}
            </div>

            <Pagination 
                page={pagination.page} 
                pages={pagination.pages} 
                onPageChange={handlePageChange} 
            />
        </div>
    );
};

export default AdminLogs;
